import React, { useState, useEffect, useRef } from "react";
import { Notification } from "@telia/teddy";
import { useDispatch, useSelector } from "react-redux";
import { FORM_ID } from "../../../../../constants";
import pages from "../../../../../constants/pages";
import { RootState } from "../../../../../models";
import { Agent } from "../../../../../models/state";
import { FieldSet, Input } from "../../../forms";
import { isTypeLoading } from "../../../loading/state/selectors";
import { RapidForm } from "../../../rapidform";
import { VALIDATION_FIELD } from "../../../rapidform/formValidation";
import { lookupCustomerByPhoneNumber } from "../../state/actions/customerActions";
import { RETAILER_LOOKUP_CUSTOMER_BY_PHONE_NUMBER } from "../../state/actionTypes";
import { getFormById } from "../../state/selectors/formSelectors";

const ExistingCustomerStep: React.FC<{ agent?: Agent | null; onHasError?: (hasError: boolean) => void }> = ({
    agent,
    onHasError,
}) => {
    const dispatch = useDispatch();
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const form = useSelector((state: RootState) => getFormById(state, FORM_ID.RETAILER_LOOKUP));
    const isLoading = useSelector((state: RootState) => isTypeLoading(state, RETAILER_LOOKUP_CUSTOMER_BY_PHONE_NUMBER));
    const inputValue = form?.values?.details?.[VALIDATION_FIELD.PHONE_OR_MBB] || "";
    const errorPhoneRef = useRef<string | null>(null);
    const formWrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const input = formWrapperRef.current?.querySelector<HTMLInputElement>("input");
        input?.focus();
    }, []);

    const handleSubmit = async () => {
        try {
            setErrorMessage(null);
            const result = await dispatch(lookupCustomerByPhoneNumber(inputValue, agent));
            if (result.success) {
                window.location.assign(pages.RETAILER.ORDER.CUSTOMER.URL);
            } else {
                errorPhoneRef.current = inputValue;
                setErrorMessage("Det oppsto en feil ved oppslag av kunden");
                onHasError?.(true);
            }
        } catch (err) {
            errorPhoneRef.current = inputValue;
            if (err && typeof err === "object" && "displayableMessage" in err) {
                setErrorMessage(err.displayableMessage as string);
            } else {
                setErrorMessage(err instanceof Error ? err.message : "Det oppsto en ukjent feil");
            }
            onHasError?.(true);
        }
    };

    useEffect(() => {
        if (errorPhoneRef.current !== null && inputValue !== errorPhoneRef.current) {
            errorPhoneRef.current = null;
            setErrorMessage(null);
            onHasError?.(false);
        }
    }, [inputValue, onHasError]);

    useEffect(() => {
        const digits = inputValue.replace(/\s/g, "").length;
        if ((digits === 8 || digits === 12) && !isLoading && errorPhoneRef.current !== inputValue) {
            handleSubmit();
        }
    }, [inputValue]);

    useEffect(() => {
        return () => {
            onHasError?.(false);
        };
    }, [onHasError]);

    return (
        <div ref={formWrapperRef}>
            <RapidForm
                id={FORM_ID.RETAILER_LOOKUP}
                action={"#"}
                values={form?.values || {}}
                visible={form?.visible || {}}
                started={form?.started || {}}
                errors={form?.errors || {}}
                onSubmit={handleSubmit}
            >
                <FieldSet name="details">
                    <Input
                        type="text"
                        name={VALIDATION_FIELD.PHONE_OR_MBB}
                        label="Telefonnummer / MBB-nummer"
                        disabled={isLoading}
                        data-di-mask
                    />
                </FieldSet>
            </RapidForm>
            {errorMessage && (
                <div className="subscription-user-info__error">
                    <Notification type="tile" variant="error" mb="300">
                        <Notification.Icon />
                        <Notification.Text>{errorMessage}</Notification.Text>
                    </Notification>
                </div>
            )}
        </div>
    );
};

export default ExistingCustomerStep;
